import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { SelectField } from '../../components/ui/SelectField';
import { Combobox } from '../../components/ui/Combobox';
import { CONTACT_TITLES } from '../../types/contact';
import type {
  AccountLink,
  Contact,
  ContactInput,
  ContactTitle,
} from '../../types/contact';
import { createContact, updateContact } from './api';
import { listAccounts } from '../accounts/api';
import { ContactAvatar } from './ContactAvatar';

type Mode =
  | { kind: 'create'; prefillAccountId?: string }
  | { kind: 'edit'; contact: Contact };

// ⚠️ From the shared vocabulary — see CONTACT_TITLES. The portal's greeting
// strips these, so a title that existed only here would be greeted by name.
const TITLES: readonly ContactTitle[] = CONTACT_TITLES;

const inputClass =
  'w-full rounded border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400';
const labelClass = 'block text-sm font-medium text-slate-700 mb-1';

type FormState = {
  firstName: string;
  lastName: string;
  title: ContactTitle | '';
  email: string;
  phone: string;
  photoUrl: string;
  birthdayDay: string;
  birthdayMonth: string;
  notes: string;
  // Ad Preview Stage 3 §1.1. Contact-level channel opt-ins. Edited via
  // the "Comms channels" group below.
  channelEmail: boolean;
  channelSMS: boolean;
  accountLinks: AccountLink[];
};

function initialState(mode: Mode): FormState {
  if (mode.kind === 'create') {
    // When created from an Account's "Add contact" button, prefill a single
    // AccountLink so the new contact is linked to that account on save.
    // Admin can tune comms toggles / primary flag before saving.
    const accountLinks: AccountLink[] = mode.prefillAccountId
      ? [
          {
            accountId: mode.prefillAccountId,
            jobTitle: null,
            isPrimary: false,
            receivesServiceCommunications: false,
            receivesBillingCommunications: false,
            receivesCampaignPreviews: false,
          },
        ]
      : [];
    return {
      firstName: '',
      lastName: '',
      title: '',
      email: '',
      phone: '',
      photoUrl: '',
      birthdayDay: '',
      birthdayMonth: '',
      notes: '',
      // Stage 3 §1.1. New contacts default both channels off; the AM
      // opts them in explicitly once email/phone are filled.
      channelEmail: false,
      channelSMS: false,
      accountLinks,
    };
  }
  const c = mode.contact;
  return {
    firstName: c.firstName,
    lastName: c.lastName ?? '',
    title: c.title ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    photoUrl: c.photoUrl ?? '',
    birthdayDay: c.birthday?.day ? String(c.birthday.day) : '',
    birthdayMonth: c.birthday?.month ? String(c.birthday.month) : '',
    notes: c.notes ?? '',
    channelEmail: c.channelEmail,
    channelSMS: c.channelSMS,
    accountLinks: c.accountLinks.map((l) => ({ ...l })),
  };
}

function toInput(s: FormState): ContactInput {
  const trimOrNull = (v: string) => (v.trim() === '' ? null : v.trim());
  const day = Number(s.birthdayDay);
  const month = Number(s.birthdayMonth);
  const birthday =
    s.birthdayDay && s.birthdayMonth && day >= 1 && day <= 31 && month >= 1 && month <= 12
      ? { day, month }
      : null;
  // Stage 3 §1.1. Channels are gated by their corresponding contact field:
  // if email is empty there's no destination to send to, so we force
  // channelEmail off regardless of the checkbox state. Mirror for SMS.
  // The disabled tooltip in the UI matches this gate so the wire shape is
  // never out of sync with what the AM actually saw.
  const email = trimOrNull(s.email);
  const phone = trimOrNull(s.phone);
  return {
    firstName: s.firstName.trim(),
    lastName: trimOrNull(s.lastName),
    title: (s.title || null) as ContactTitle | null,
    email,
    phone,
    photoUrl: trimOrNull(s.photoUrl),
    birthday,
    notes: trimOrNull(s.notes),
    channelEmail: s.channelEmail && email !== null,
    channelSMS: s.channelSMS && phone !== null,
    accountLinks: s.accountLinks.map((l) => ({ ...l, jobTitle: l.jobTitle?.trim() || null })),
  };
}

export function ContactForm({ mode }: { mode: Mode }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const [state, setState] = useState<FormState>(() => initialState(mode));
  const [error, setError] = useState<string | null>(null);
  const [pendingAccountId, setPendingAccountId] = useState<string>('');

  const createMut = useMutation({
    mutationFn: (input: ContactInput) => createContact(input),
    onSuccess: async (contact) => {
      await qc.invalidateQueries({ queryKey: ['contacts'] });
      navigate(`/contacts/${contact.id}`);
    },
    onError: (e: Error) => setError(e.message),
  });
  const updateMut = useMutation({
    mutationFn: (input: ContactInput) => {
      if (mode.kind !== 'edit') throw new Error('updateMut invoked in create mode');
      return updateContact(mode.contact.id, input);
    },
    onSuccess: async (contact) => {
      await qc.invalidateQueries({ queryKey: ['contacts'] });
      await qc.invalidateQueries({ queryKey: ['contact', contact.id] });
      navigate(`/contacts/${contact.id}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  const linkedAccountIds = useMemo(
    () => new Set(state.accountLinks.map((l) => l.accountId)),
    [state.accountLinks],
  );
  const availableAccounts = useMemo(
    () => (accounts.data ?? []).filter((a) => !linkedAccountIds.has(a.id)),
    [accounts.data, linkedAccountIds],
  );
  const accountName = (id: string) =>
    (accounts.data ?? []).find((a) => a.id === id)?.name ?? id;

  if (accounts.isLoading) return <CenteredSpinner />;

  const addLink = (accountId: string) => {
    if (!accountId) return;
    setState((s) => ({
      ...s,
      accountLinks: [
        ...s.accountLinks,
        {
          accountId,
          jobTitle: null,
          isPrimary: false,
          receivesServiceCommunications: false,
          receivesBillingCommunications: false,
          receivesCampaignPreviews: false,
        },
      ],
    }));
    setPendingAccountId('');
  };
  const removeLink = (accountId: string) => {
    setState((s) => ({ ...s, accountLinks: s.accountLinks.filter((l) => l.accountId !== accountId) }));
  };
  const updateLink = (accountId: string, patch: Partial<AccountLink>) => {
    setState((s) => ({
      ...s,
      accountLinks: s.accountLinks.map((l) => (l.accountId === accountId ? { ...l, ...patch } : l)),
    }));
  };

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (state.accountLinks.length === 0) {
      if (!window.confirm('This contact has no linked accounts. Save anyway?')) return;
    }
    const input = toInput(state);
    if (mode.kind === 'create') createMut.mutate(input);
    else updateMut.mutate(input);
  }

  const submitting = createMut.isPending || updateMut.isPending;

  return (
    <form onSubmit={submit} className="space-y-6 max-w-4xl">
      <h1 className="text-2xl font-semibold">
        {mode.kind === 'create' ? 'New contact' : `Edit: ${mode.contact.firstName}`}
      </h1>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>
      )}

      <section className="bg-white rounded-md border border-slate-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold">Identity</h2>
        <div>
          <label className={labelClass}>Title</label>
          <SelectField
            aria-label="Title"
            value={state.title}
            onValueChange={(v) => setState((s) => ({ ...s, title: v as ContactTitle | '' }))}
            options={[{ value: '', label: '—' }, ...TITLES.map((t) => ({ value: t, label: t }))]}
          />
        </div>
        <div>
          <label className={labelClass}>First name <span className="text-red-500">*</span></label>
          <input className={inputClass} value={state.firstName} onChange={(e) => setState((s) => ({ ...s, firstName: e.target.value }))} required />
        </div>
        <div>
          <label className={labelClass}>Last name</label>
          <input className={inputClass} value={state.lastName} onChange={(e) => setState((s) => ({ ...s, lastName: e.target.value }))} />
        </div>
        <div>
          <label className={labelClass}>Email</label>
          <input type="email" className={inputClass} value={state.email} onChange={(e) => setState((s) => ({ ...s, email: e.target.value }))} />
        </div>
        <div>
          <label className={labelClass}>Phone</label>
          <input className={inputClass} value={state.phone} onChange={(e) => setState((s) => ({ ...s, phone: e.target.value }))} />
        </div>
        <div>
          <label className={labelClass}>Photo URL</label>
          <input
            className={inputClass}
            value={state.photoUrl}
            onChange={(e) => setState((s) => ({ ...s, photoUrl: e.target.value }))}
            placeholder="https://... (paste a Drive or external URL)"
          />
          <div className="mt-2 flex items-center gap-3">
            <ContactAvatar
              contact={{
                firstName: state.firstName,
                lastName: state.lastName || null,
                photoUrl: state.photoUrl || null,
              }}
              size="md"
            />
            <p className="text-xs text-slate-500">
              Preview. Falls back to initials when no URL is set or the image can't load.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Birthday day</label>
            <input className={inputClass} type="number" min={1} max={31} value={state.birthdayDay} onChange={(e) => setState((s) => ({ ...s, birthdayDay: e.target.value }))} />
          </div>
          <div>
            <label className={labelClass}>Birthday month</label>
            <input className={inputClass} type="number" min={1} max={12} value={state.birthdayMonth} onChange={(e) => setState((s) => ({ ...s, birthdayMonth: e.target.value }))} />
          </div>
        </div>
        <div>
          <label className={labelClass}>Notes</label>
          <textarea className={inputClass} rows={3} value={state.notes} onChange={(e) => setState((s) => ({ ...s, notes: e.target.value }))} />
        </div>
      </section>

      {/* Stage 3 §1.1 + §2.1. Contact-level channel opt-ins, gated by the
          presence of email / phone above. Channel checkboxes disable when
          the corresponding field is empty -- there's no destination to
          send to. The tooltip surfaces the why for AMs. */}
      <section className="bg-white rounded-md border border-slate-200 p-6 space-y-3">
        <div>
          <h2 className="text-lg font-semibold">Comms channels</h2>
          <p className="text-xs text-slate-500 mt-1">
            Channels this contact has opted in to. A channel must be on AND
            its corresponding field (email / phone) filled before any
            preview send can reach them via that channel.
          </p>
        </div>
        <div className="flex flex-wrap gap-6 text-sm">
          <label
            className="inline-flex items-center gap-2"
            title={
              state.email.trim() === ''
                ? 'Add an email above to enable this channel.'
                : undefined
            }
          >
            <input
              type="checkbox"
              checked={state.channelEmail}
              onChange={(e) => setState((s) => ({ ...s, channelEmail: e.target.checked }))}
              disabled={state.email.trim() === ''}
            />
            <span aria-hidden>📧</span>
            <span>Email</span>
          </label>
          <label
            className="inline-flex items-center gap-2"
            title={
              state.phone.trim() === ''
                ? 'Add a phone number above to enable this channel.'
                : undefined
            }
          >
            <input
              type="checkbox"
              checked={state.channelSMS}
              onChange={(e) => setState((s) => ({ ...s, channelSMS: e.target.checked }))}
              disabled={state.phone.trim() === ''}
            />
            <span aria-hidden>📱</span>
            <span>SMS</span>
          </label>
        </div>
      </section>

      <section className="bg-white rounded-md border border-slate-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold">Account links</h2>
        {/* ⚠️ ONE LINE, NOT A WARNING DIALOG. Account links decide what this
            person sees in the client portal (src/lib/portalScope.ts), and staff
            edit them routinely without thinking of it as a permissions action —
            so the consequence is stated where the edit happens. A dialog would
            get dismissed; a sentence gets read once and remembered. It says "if
            they have portal access" because most contacts do not, and implying
            every link publishes numbers to somebody would be its own untruth. */}
        <p className="-mt-2 text-sm text-slate-600">
          If this contact has client-portal access, these links are what decide which accounts
          they can see. Removing one takes that account off their portal.
        </p>
        {state.accountLinks.length === 0 ? (
          <p className="text-sm text-slate-500">No accounts linked yet.</p>
        ) : (
          <div className="space-y-3">
            {state.accountLinks.map((l) => (
              <div key={l.accountId} className="border border-slate-200 rounded p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{accountName(l.accountId)}</span>
                  <Button type="button" variant="outline" onClick={() => removeLink(l.accountId)}>Remove</Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Job title</label>
                    <input
                      className={inputClass}
                      value={l.jobTitle ?? ''}
                      onChange={(e) => updateLink(l.accountId, { jobTitle: e.target.value })}
                      placeholder="Owner, Marketing Manager, …"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                  <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={l.isPrimary} onChange={(e) => updateLink(l.accountId, { isPrimary: e.target.checked })} />
                    Primary contact for this account
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={l.receivesServiceCommunications} onChange={(e) => updateLink(l.accountId, { receivesServiceCommunications: e.target.checked })} />
                    Service comms
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={l.receivesBillingCommunications} onChange={(e) => updateLink(l.accountId, { receivesBillingCommunications: e.target.checked })} />
                    Billing comms
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={l.receivesCampaignPreviews} onChange={(e) => updateLink(l.accountId, { receivesCampaignPreviews: e.target.checked })} />
                    Campaign previews
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
        {availableAccounts.length > 0 && (
          <div className="flex items-center gap-3">
            <Combobox
              aria-label="Select an account to link"
              className="flex-1"
              searchPlaceholder="Search accounts…"
              placeholder="Select an account to link…"
              value={pendingAccountId}
              onValueChange={setPendingAccountId}
              options={[
                { value: '', label: 'Select an account to link…' },
                ...availableAccounts.map((a) => ({
                  value: a.id,
                  label: a.name,
                  description: a.accountType,
                  keywords: a.accountType,
                })),
              ]}
            />
            <Button type="button" variant="outline" onClick={() => addLink(pendingAccountId)} disabled={!pendingAccountId}>
              Add link
            </Button>
          </div>
        )}
      </section>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : mode.kind === 'create' ? 'Create contact' : 'Save changes'}
        </Button>
        <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
      </div>
    </form>
  );
}
